import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Utensils, X, AlertTriangle, Info } from 'lucide-react';
import { allergyMatchesTerm, normalizeAllergenTerm, allergyHitsIngredient, ingredientsBlockingRestriction, keywordsForRestriction } from '../../../shared/allergenReference.js';
import { dishStatusFor, removalSuggestionsFor, removalTargetOf } from '../../../shared/dishCompatibility.js';
import { publicEvenementielApi } from '../../lib/api';

interface DishVariant { id: string; label: string; resolves_restrictions: string[]; }
interface DishComponent { name: string; removable: boolean; }
interface DishIngredient { name: string; removable: boolean; components?: DishComponent[]; incompatible_tags?: string[]; }
interface Dish { id: string; name: string; description: string; ingredients: DishIngredient[]; allergens: string[]; dietary_tags: string[]; incompatible_tags: string[]; variants: DishVariant[]; traces: string[]; remaining: number | null; }
interface CategoryGroup { category: string; dishes: Dish[]; }
interface AltOfferDish { id: string; name: string; description: string; ingredients: DishIngredient[]; allergens: string[]; traces: string[]; }
interface AltDishOffer { category: string; dishes: AltOfferDish[]; }
interface GuestInfo {
    event: { name: string; date: string | null };
    categories: CategoryGroup[];
    guest_choice_mode: 'convive' | 'organisateur';
    restriction_options: string[];
    allow_alt_dish_request?: boolean;
    ingredient_options: string[];
    custom_allergen_terms?: { family: string; term: string }[];
    locked?: boolean;
    closed_no_response?: boolean;
    alt_dish_offer?: AltDishOffer | null;
    instruction?: string;
    /** Mot d'accueil de l'organisateur (Portail Organisateur, lot 10) — affiché avant tout le reste. */
    organizer_welcome?: string;
    guest: { first_name: string; menu_choices: Record<string, string>; menu_choice_variants: Record<string, string>; menu_choice_removals?: Record<string, string[]>; menu_skipped_categories?: string[]; restrictions: string[]; allergies: string[]; medical_note?: string; responded_at: string | null };
}

const PoweredByFooter: React.FC = () => (
    <a href="https://l-iamani.com" target="_blank" rel="noopener noreferrer"
        className="mt-5 inline-flex items-center gap-1 text-[11px] text-content-muted hover:text-content transition-colors">
        Propulsé par <span className="font-bold">L'IAmani</span>
    </a>
);

/** Page mobile du convive (Phase 4 de "Menus & Convives") — atteinte via le lien individuel envoyé par
 *  email. Ne collecte QUE le choix de menu et les restrictions/allergies : volontairement AUCUN bouton
 *  "je confirme ma présence", exigence explicite de la note d'architecture d'origine. Toujours modifiable
 *  tant que la collecte n'est pas verrouillée (pas encore de date limite dans cette phase). */
export const EvenementielGuestPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [phase, setPhase] = React.useState<'loading' | 'notfound' | 'ready'>('loading');
    const [info, setInfo] = React.useState<GuestInfo | null>(null);
    const [choices, setChoices] = React.useState<Record<string, string>>({});
    const [variantChoices, setVariantChoices] = React.useState<Record<string, string>>({});
    // Ingredients que le convive demande a retirer, par categorie : {"Plat": ["Sauce au beurre blanc"]}.
    const [removals, setRemovals] = React.useState<Record<string, string[]>>({});
    // Categories ou le convive a explicitement declare "je ne prendrai rien" (ex: allergie qui exclut TOUS
    // les desserts) - propose seulement quand aucun plat de la categorie ne lui convient. Idee d'Emeni
    // (2026-08-24) : "autoriser a ne rien prendre dans une categorie".
    const [skippedCategories, setSkippedCategories] = React.useState<string[]>([]);
    // Categories ou le convive DEMANDE une alternative, distinct de celles ou il renonce : renoncer
    // n'attend rien, demander attend une reponse de l'etablissement. Demande d'Emeni (2026-08-24).
    const [altRequestedCategories, setAltRequestedCategories] = React.useState<string[]>([]);
    const [restrictions, setRestrictions] = React.useState<string[]>([]);
    const [customRestriction, setCustomRestriction] = React.useState('');
    const [allergies, setAllergies] = React.useState<string[]>([]);
    const [allergyDraft, setAllergyDraft] = React.useState('');
    const [medicalNote, setMedicalNote] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);
    const [error, setError] = React.useState('');
    // Choix d'un plat de secours proposé exceptionnellement par l'établissement (voir alt_dish_offer) —
    // indépendant du formulaire principal, utilisable même verrouillé. Demande d'Emeni (2026-08-25).
    const [altDishChoice, setAltDishChoice] = React.useState<string | null>(null);
    const [altDishSubmitting, setAltDishSubmitting] = React.useState(false);
    const [altDishSubmitted, setAltDishSubmitted] = React.useState(false);
    const [altDishError, setAltDishError] = React.useState('');

    // Page publique toujours en mode clair, quel que soit le thème système/navigateur du convive — même
    // pattern que DocumentationResponse.tsx. Demande explicite d'Emeni (2026-08-22).
    React.useEffect(() => {
        const root = document.documentElement;
        const previousTheme = root.getAttribute('data-theme');
        const wasDark = root.classList.contains('dark');
        root.setAttribute('data-theme', 'light');
        root.classList.remove('dark');
        root.classList.add('light');
        return () => {
            if (previousTheme) root.setAttribute('data-theme', previousTheme); else root.removeAttribute('data-theme');
            root.classList.toggle('dark', wasDark);
            root.classList.toggle('light', !wasDark);
        };
    }, []);

    React.useEffect(() => {
        if (!token) return;
        publicEvenementielApi.getGuestPage(token).then((data: any) => {
            if (data?.error) { setPhase('notfound'); return; }
            setInfo(data);
            setChoices(data.guest?.menu_choices || {});
            setVariantChoices(data.guest?.menu_choice_variants || {});
            setRemovals(data.guest?.menu_choice_removals || {});
            setSkippedCategories(data.guest?.menu_skipped_categories || []);
            setAltRequestedCategories(data.guest?.menu_alt_requested || []);
            setRestrictions((data.guest?.restrictions || []).filter((r: string) => data.restriction_options.includes(r)));
            setCustomRestriction((data.guest?.restrictions || []).find((r: string) => !data.restriction_options.includes(r)) || '');
            setAllergies(data.guest?.allergies || []);
            setMedicalNote(data.guest?.medical_note || '');
            setPhase('ready');
        }).catch(() => setPhase('notfound'));
    }, [token]);

    // Le convive laisse souvent sa page ouverte pendant que l'établissement ajuste encore ses plats, ses
    // allergènes ou ses restrictions. Sans rafraîchissement, il répondrait sur une version périmée et
    // pourrait valider un plat que le système saurait désormais incompatible. On recharge donc au retour sur
    // l'onglet et toutes les deux minutes — mais UNIQUEMENT les données de référence : ses choix, allergies
    // et cases cochées ne sont jamais réécrits, sinon une saisie en cours serait perdue sous ses doigts.
    // Demande d'Emeni (2026-08-23) : "quand je modifie les allergènes et restrictions ça doit tout de suite
    // se mettre à jour sur le formulaire que les convives ont reçu".
    React.useEffect(() => {
        if (!token || phase !== 'ready') return;
        let stopped = false;
        const refresh = () => {
            if (document.hidden) return;
            publicEvenementielApi.getGuestPage(token).then((data: any) => {
                if (stopped || !data || data.error) return;
                setInfo((prev) => (prev ? { ...data, guest: prev.guest } : data));
            }).catch(() => { /* hors ligne : on garde la version affichée */ });
        };
        const onVisible = () => { if (!document.hidden) refresh(); };
        document.addEventListener('visibilitychange', onVisible);
        const timer = setInterval(refresh, 120000);
        return () => { stopped = true; document.removeEventListener('visibilitychange', onVisible); clearInterval(timer); };
    }, [token, phase]);

    // Les mots-clés des restrictions non-allergènes (Halal, Végétarien…) viennent désormais du référentiel
    // partagé et non plus d'une table figée ici : ils sont visibles et complétables depuis l'écran
    // « Allergènes & restrictions ». Emeni ne pouvait ni les consulter ni les corriger.
    const keywordsFor = (r: string) => keywordsForRestriction(r, info?.custom_allergen_terms);

    // Avertissement d'incompatibilité affiché dès la sélection d'un plat — jamais bloquant (le convive reste
    // libre de son choix, ex. il connaît sa tolérance réelle), juste une explication claire pour la
    // commerciale/cuisine au moment de la validation humaine. Priorité stricte pour chaque restriction cochée :
    // 1) "restrictions incompatibles" du plat (saisie explicite de l'établissement) → warning garanti ;
    // 2) "restrictions couvertes" du plat (saisie explicite) → jamais de warning ;
    // 3) sinon, repli sur les mots-clés (seulement pour les restrictions du socle historique, qu'on sait
    //    interpréter — une restriction personnalisée non reconnue n'est jamais accusée à tort par mot-clé).
    // Plus une allergie déclarée en texte libre retrouvée (recherche insensible à la casse) dans les
    // ingrédients ou allergènes du plat.
    // Une variante du plat lui-même (ex: "Sans viande", "Sans viande et sans sauce" sur "Fondant de veau")
    // peut résoudre spécifiquement UNE restriction (ex: "Sans viande") — le convive doit alors explicitement
    // CHOISIR cette variante (jamais un simple texte d'info) pour être conscient qu'il change son plat.
    // Demande explicite d'Emeni (2026-08-22) : "à chaque fois qu'il y a une alternative le convive doit bien
    // sélectionner l'alternative pour être conscient qu'il prend ça [...] fais ça proprement et simple à
    // comprendre". Les variantes sont maintenant rattachées directement au plat (pas des plats séparés).
    const variantFor = (dish: Dish, restriction: string): DishVariant | undefined =>
        (dish.variants || []).find((v) => v.resolves_restrictions.includes(restriction));

    interface DishWarning { text: string; variant?: DishVariant; removableIngredient?: boolean; blocking?: boolean; trace?: boolean; removalSuggestion?: string }

    /** Ingrédients du plat que le convive a demandé à retirer, pour cette catégorie. */
    const removalsFor = (category: string) => removals[category] || [];

    /**
     * Ingrédients à écarter pour que ce plat convienne au convive, déduits de ses restrictions ET de ses
     * allergies. Le résultat pointe toujours la ligne RETIRABLE, jamais le composant : on ne peut pas retirer
     * le beurre d'une sauce, on retire la sauce. Idée d'Emeni (2026-08-23) : "ça écarte directement la sauce
     * et non pas l'ingrédient concerné, parce qu'on ne peut pas retirer l'ingrédient mais on peut retirer la
     * sauce".
     */
    // Cible effective d'un retrait, état d'un plat et suggestions de retrait vivent désormais dans
    // shared/dishCompatibility.js : l'écran de configuration du menu (côté établissement) s'en sert pour
    // annoncer "aucun plat possible pour un convive sans lactose", et cette alerte ne dirait pas la même
    // chose que cette page si la règle était recopiée des deux côtés. Extrait le 2026-08-24, comportement
    // inchangé — le profil (restrictions, allergies, termes personnalisés) est simplement passé en argument
    // au lieu d'être capturé par fermeture.
    const guestProfile = { restrictions, allergies, customTerms: info?.custom_allergen_terms };
    const removalTarget = (hit: { ingredient: DishIngredient; via: DishComponent | null }) => removalTargetOf(hit);
    const getRemovalSuggestions = (dish: Dish) => removalSuggestionsFor(dish, guestProfile);
    const getDishStatus = (dish: Dish): 'ok' | 'adaptable' | 'bloque' => dishStatusFor(dish, guestProfile);

    const getDishWarnings = (dish: Dish, selectedVariantId?: string, category?: string): DishWarning[] => {
        const warnings: DishWarning[] = [];
        const dishHaystack = `${dish.name} ${dish.description} ${dish.ingredients.map((i) => i.name).join(' ')}`.toLowerCase();
        const selectedVariant = (dish.variants || []).find((v) => v.id === selectedVariantId);
        const removed = category ? removalsFor(category) : [];
        const isRemoved = (name: string) => removed.some((r) => r.toLowerCase() === name.toLowerCase());
        restrictions.forEach((r) => {
            if (selectedVariant && selectedVariant.resolves_restrictions.includes(r)) return;
            // Ce que le convive a déjà accepté de retirer ne doit plus être signalé comme un problème :
            // sinon "sans sauce" coché, l'avertissement "ne correspond pas à sans lactose" reste affiché et
            // laisse croire que la demande n'a pas été prise en compte.
            const covered = dish.dietary_tags.includes(r);
            const blockers = ingredientsBlockingRestriction(r, dish.ingredients, covered ? [] : (keywordsFor(r)), info.custom_allergen_terms)
                .filter((h) => !isRemoved(removalTarget(h).name));
            if (covered && blockers.length === 0) return;
            if (blockers.length === 0) {
                // Aucun ingrédient identifié : on retombe sur la déclaration au niveau du plat.
                if (dish.incompatible_tags.includes(r)) warnings.push({ text: `Non compatible avec « ${r} »`, blocking: true });
                return;
            }
            const targets = blockers.map((h) => removalTarget(h));
            const removable = targets.filter((t) => t.removable);
            // Une restriction bloque au même titre qu'une allergie : le convive doit soit faire retirer ce
            // qui gêne, soit changer de plat. Seules les traces n'empêchent jamais de valider (voir plus
            // bas). Demande d'Emeni (2026-08-23) : "ça doit bloquer dans tous les cas, la seule chose qui ne
            // bloque pas c'est les traces".
            if (removable.length === targets.length) {
                warnings.push({
                    text: `« ${r} » — cochez ${removable.map((t) => `« sans ${t.name.toLowerCase()} »`).join(' et ')} pour continuer`,
                    removalSuggestion: removable.map((t) => t.name).join(', '),
                    blocking: true,
                });
            } else {
                warnings.push({ text: `Non compatible avec « ${r} »`, blocking: true });
            }
        });
        // Allergie déclarée par le convive, comparée aux ingrédients réels du plat (pas un mot-clé dans le
        // nom/description) — si l'ingrédient est marqué "possible de retirer" par l'établissement, la cuisine
        // le retire et le convive en est simplement informé ; sinon, l'envoi est bloqué pour cette catégorie.
        // Demande explicite d'Emeni (2026-08-23) : "si jamais l'allergie est bien présente dans un ingrédient
        // [...] si possible de le retirer, on informe le convive [...] si ce n'est pas possible il est
        // informé également [et bloqué]".
        // La correspondance passe par le référentiel d'allergènes partagé (shared/allergenReference.js) et
        // non plus par une égalité de texte : un convive déclarant "arachide" doit être alerté par un topping
        // saisi "cacahuète". Avant, il ne l'était pas — ni averti, ni bloqué, et la cuisine ne voyait rien.
        allergies.forEach((a) => {
            // L'allergène peut venir de l'ingrédient lui-même ou d'un de ses composants : le lait du "beurre
            // blanc" doit se lire sur la ligne "Sauce au beurre blanc", seule chose que la cuisine peut
            // réellement retirer de l'assiette.
            let hit: { ingredient: DishIngredient; via: DishComponent | null } | null = null;
            for (const i of dish.ingredients) {
                hit = allergyHitsIngredient(a, i, info.custom_allergen_terms);
                if (hit) break;
            }
            if (!hit) return;
            const target = removalTarget(hit);
            // Déjà retiré à la demande du convive : plus rien à signaler, l'assiette ne le contiendra pas.
            if (isRemoved(target.name)) return;
            // Le nom réel (ingrédient ou composant précis) est rappelé quand il diffère du mot déclaré,
            // sinon "vous avez déclaré arachide" en face de "contient arachide" laisse croire à une erreur
            // de saisie. Message court, centré sur CE que le convive a déclaré : il n'a pas besoin de la
            // composition du plat, seulement de savoir que son allergie est en cause et de ce qu'il doit
            // faire. Demande d'Emeni (2026-08-23) : "pas besoin de lister tout ça, il faut juste dire que ce
            // n'est pas compatible avec son allergie carotte".
            warnings.push({
                text: target.removable
                    ? `Allergie « ${a} » — cochez « sans ${target.name.toLowerCase()} » pour continuer`
                    : `Non compatible avec votre allergie « ${a} »`,
                removableIngredient: target.removable,
                // Bloquant tant que le retrait n'est pas coché, même sur un ingrédient retirable : sans ce
                // geste, aucune consigne ne part en cuisine et l'assiette serait servie telle quelle alors
                // que le convive croit son allergie prise en compte. Une fois la case cochée, cet
                // avertissement disparaît (voir isRemoved plus haut) et l'envoi se débloque. Demande d'Emeni
                // (2026-08-23) : "il faut que le convive coche bien « sans légumes » pour continuer".
                blocking: true,
                removalSuggestion: target.removable ? target.name : undefined,
            });
        });
        // Traces possibles : contamination croisée en cuisine, l'allergène n'est PAS un ingrédient du plat.
        // Jamais bloquant, même si l'ingrédient équivalent le serait — le convive est informé et reste libre
        // de choisir en connaissance de cause. Un plat qui contient déjà l'allergène en ingrédient ne déclenche
        // pas en plus l'avertissement de trace : le message "contient" est plus fort et suffit. Demande
        // explicite d'Emeni (2026-08-23) : "ça ne doit pas les bloquer, par contre ça doit les prévenir des
        // traces possibles inévitables".
        allergies.forEach((a) => {
            if (dish.ingredients.some((i) => allergyHitsIngredient(a, i, info.custom_allergen_terms))) return;
            const trace = (dish.traces || []).find((t) => allergyMatchesTerm(a, t, info.custom_allergen_terms));
            if (!trace) return;
            warnings.push({ text: `Peut contenir des traces de ${trace.toLowerCase()} (allergie déclarée)`, trace: true });
        });
        return warnings;
    };

    const toggleRestriction = (label: string) => setRestrictions((prev) => prev.includes(label) ? prev.filter((r) => r !== label) : [...prev, label]);
    const addAllergy = (value: string) => {
        const v = value.trim();
        if (!v || allergies.some((x) => x.toLowerCase() === v.toLowerCase())) { setAllergyDraft(''); return; }
        setAllergies((prev) => [...prev, v]);
        setAllergyDraft('');
    };

    const handleSubmit = async () => {
        if (!token) return;
        setSubmitting(true);
        setError('');
        try {
            const allRestrictions = customRestriction.trim() ? [...restrictions, customRestriction.trim()] : restrictions;
            // Seuls les retraits portant sur un plat effectivement choisi sont envoyés : sans ce filtre, une
            // case cochée puis un changement de plat laisserait une consigne "sans sauce" sur une assiette qui
            // n'en a pas, et la cuisine chercherait quoi retirer.
            const cleanRemovals: Record<string, string[]> = {};
            Object.entries(removals).forEach(([category, names]) => {
                const dish = (info.categories.find((c) => c.category === category)?.dishes || []).find((d) => d.id === choices[category]);
                if (!dish) return;
                const kept = (names || []).filter((n) => dish.ingredients.some((i) =>
                    (i.removable && i.name.toLowerCase() === n.toLowerCase()) ||
                    (i.components || []).some((c) => c.removable && c.name.toLowerCase() === n.toLowerCase())));
                if (kept.length) cleanRemovals[category] = kept;
            });
            const res: any = await publicEvenementielApi.submitGuestResponse(token, { menu_choices: choices, variant_choices: variantChoices, removals: cleanRemovals, skipped_categories: skippedCategories, alt_requested_categories: altRequestedCategories, restrictions: allRestrictions, allergies, medical_note: medicalNote });
            if (res?.error) { setError(res.error); return; }
            setSubmitted(true);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'envoi. Merci de réessayer.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAltDishSubmit = async () => {
        if (!token || !altDishChoice) return;
        setAltDishSubmitting(true);
        setAltDishError('');
        try {
            const res: any = await publicEvenementielApi.submitAlternativeDishChoice(token, altDishChoice);
            if (res?.error) { setAltDishError(res.error); return; }
            setAltDishSubmitted(true);
        } catch (e: any) {
            setAltDishError(e?.message || 'Erreur lors de l\'envoi. Merci de réessayer.');
        } finally {
            setAltDishSubmitting(false);
        }
    };

    if (phase === 'loading') return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-content-muted" size={28} /></div>;
    if (phase === 'notfound' || !info) return (
        <div className="min-h-screen flex items-center justify-center p-6 text-center">
            <p className="text-content-muted">Ce lien n'est plus valide.</p>
        </div>
    );

    if (submitted) return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center gap-3">
            <CheckCircle2 size={40} className="text-brand-500" />
            <h1 className="text-lg font-bold text-content">Merci {info.guest.first_name} !</h1>
            <p className="text-sm text-content-muted max-w-xs">Votre choix a bien été enregistré. Vous pouvez revenir sur ce lien à tout moment pour le modifier.</p>
            <PoweredByFooter />
        </div>
    );

    const closedNoResponse = !!info.closed_no_response;
    if (closedNoResponse) return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center gap-3">
            <AlertTriangle size={40} className="text-amber-500" />
            <h1 className="text-lg font-bold text-content">Réponses closes</h1>
            <p className="text-sm text-content-muted max-w-xs">Vous n'avez pas répondu à temps et les réponses sont maintenant closes — il n'est plus possible de choisir un menu ici. Merci de vous rapprocher de l'organisateur de l'événement.</p>
            <PoweredByFooter />
        </div>
    );

    if (altDishSubmitted) return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center gap-3">
            <CheckCircle2 size={40} className="text-brand-500" />
            <h1 className="text-lg font-bold text-content">Merci {info.guest.first_name} !</h1>
            <p className="text-sm text-content-muted max-w-xs">Votre choix a bien été enregistré.</p>
            <PoweredByFooter />
        </div>
    );

    const locked = !!info.locked;
    const altOffer = info.alt_dish_offer;

    // Offre exceptionnelle d'un plat de secours pour une catégorie que le convive n'a pas pu choisir (voir
    // le bouton "Je ne prendrai pas de X"). Quand les réponses sont verrouillées, c'est la SEULE chose que
    // le convive peut encore faire — remplace le formulaire normal (même principe que closedNoResponse
    // ci-dessus) plutôt que de le mêler à un formulaire par ailleurs entièrement figé.
    if (altOffer && locked) return (
        <div className="min-h-screen bg-surface px-4 py-8 flex flex-col items-center">
            <div className="w-full max-w-md space-y-5">
                <div className="text-center space-y-1">
                    <h1 className="text-xl font-bold text-content">{info.event.name}</h1>
                    <p className="text-xs text-content-muted">Bonjour {info.guest.first_name}, l'organisateur vous propose exceptionnellement un autre choix pour votre {altOffer.category.toLowerCase()}.</p>
                </div>
                {altDishError && <p className="text-xs text-red-500 font-semibold text-center">{altDishError}</p>}
                <div className="space-y-1.5">
                    {altOffer.dishes.map((d) => (
                        <button key={d.id} type="button" onClick={() => setAltDishChoice(d.id)}
                            className={`w-full text-left px-3 py-2.5 rounded-xl border text-xs transition-all ${altDishChoice === d.id ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'bg-surface-soft border-border text-content hover:border-brand-500/40'}`}>
                            <span className="font-bold block">{d.name}</span>
                            {d.description && <span className={`block text-[11px] ${altDishChoice === d.id ? 'text-brand-fg/80' : 'text-content-muted'}`}>{d.description}</span>}
                        </button>
                    ))}
                </div>
                <button onClick={handleAltDishSubmit} disabled={altDishSubmitting || !altDishChoice}
                    className="w-full py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm disabled:opacity-50">
                    {altDishSubmitting ? 'Envoi...' : 'Envoyer mon choix'}
                </button>
                <div className="flex justify-center"><PoweredByFooter /></div>
            </div>
        </div>
    );

    // Blocage configurable côté établissement : un plat choisi explicitement incompatible avec une
    // Le réglage "bloquer les incompatibilités non résolues" a été retiré : une restriction non résolue
    // bloque désormais toujours, au même titre qu'une allergie (voir getDishWarnings). Le blocage est donc
    // calculé uniquement à partir des avertissements, sans réglage par événement.

    // Blocage par ingrédient allergène TOUJOURS actif (contrairement au blocage par restriction ci-dessus,
    // configurable) — vérifié aussi côté serveur, une allergie sur un ingrédient non retirable est plus
    // sérieuse qu'une simple restriction de goût. Demande explicite d'Emeni (2026-08-23).
    const ingredientBlockingIssues: string[] = info.categories.flatMap((cat) => {
        const selectedDish = cat.dishes.find((d) => d.id === choices[cat.category]);
        if (!selectedDish) return [];
        // La catégorie est passée pour que les ingrédients déjà cochés "à retirer" ne bloquent plus : le
        // convive a résolu le problème, l'assiette partira sans eux.
        return getDishWarnings(selectedDish, variantChoices[cat.category], cat.category)
            .filter((w) => w.blocking)
            // Deux issues différentes selon que l'ingrédient est retirable ou non : cocher le retrait, ou
            // changer de plat. Envoyer tout le monde vers "choisissez un autre plat" ferait renoncer un
            // convive alors qu'une case à cocher suffisait.
            .map((w) => w.removalSuggestion
                ? `${cat.category} : ${w.text}.`
                : `${cat.category} : ${w.text} — choisissez un autre plat.`);
    });
    const blockingIssues = ingredientBlockingIssues;

    return (
        <div className="min-h-screen bg-surface px-4 py-8 flex flex-col items-center">
            <div className="w-full max-w-md space-y-5">
                <div className="text-center space-y-1">
                    <h1 className="text-xl font-bold text-content">{info.event.name}</h1>
                    {info.event.date && <p className="text-sm text-content-muted">{new Date(info.event.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>}
                    <p className="text-xs text-content-muted">
                        {info.guest_choice_mode === 'organisateur'
                            ? `Bonjour ${info.guest.first_name}, votre repas a déjà été choisi pour vous — merci de préciser vos restrictions et allergies ci-dessous.`
                            : `Bonjour ${info.guest.first_name}, merci de choisir votre repas ci-dessous.`}
                    </p>
                </div>

                {info.organizer_welcome && (
                    <p className="text-[12.5px] leading-[1.55] text-center bg-surface-soft border border-border-soft rounded-xl px-3 py-[11px] text-content whitespace-pre-wrap text-pretty">
                        {info.organizer_welcome}
                    </p>
                )}

                {info.instruction && (
                    <p className="text-xs font-semibold text-center bg-brand-500/10 border border-brand-500/20 rounded-xl p-3 text-brand-600 dark:text-brand-400 whitespace-pre-wrap">
                        {info.instruction}
                    </p>
                )}

                {locked && (
                    <p className="text-xs font-semibold text-center bg-surface-soft border border-border rounded-xl p-3 text-content-muted">
                        Les réponses sont maintenant verrouillées — votre choix ne peut plus être modifié.
                    </p>
                )}

                {/* Offre exceptionnelle affichée EN PLUS du formulaire normal quand les réponses ne sont pas
                    verrouillées — le convive garde aussi la main sur son choix habituel. */}
                {altOffer && (
                    <div className="space-y-2 bg-brand-500/5 border-2 border-brand-500/30 rounded-xl p-3">
                        <p className="text-xs font-bold text-content">L'organisateur vous propose un autre choix pour votre {altOffer.category.toLowerCase()}</p>
                        {altDishError && <p className="text-[11px] text-red-500 font-semibold">{altDishError}</p>}
                        <div className="space-y-1.5">
                            {altOffer.dishes.map((d) => (
                                <button key={d.id} type="button" onClick={() => setAltDishChoice(d.id)}
                                    className={`w-full text-left px-2.5 py-2 rounded-xl border text-xs transition-all ${altDishChoice === d.id ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'bg-surface border-border text-content hover:border-brand-500/40'}`}>
                                    <span className="font-bold block">{d.name}</span>
                                    {d.description && <span className={`block text-[11px] ${altDishChoice === d.id ? 'text-brand-fg/80' : 'text-content-muted'}`}>{d.description}</span>}
                                </button>
                            ))}
                        </div>
                        <button onClick={handleAltDishSubmit} disabled={altDishSubmitting || !altDishChoice}
                            className="w-full py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-xs disabled:opacity-50">
                            {altDishSubmitting ? 'Envoi...' : 'Envoyer ce choix'}
                        </button>
                    </div>
                )}

                {error && <p className="text-xs text-red-500 font-semibold text-center">{error}</p>}

                {/* Déclarations AVANT le choix des plats : le convive doit dire ce qu'il ne peut pas manger
                    avant de choisir, sinon il sélectionne à l'aveugle puis découvre en fin de formulaire que
                    son plat est refusé. Les plats affichent leurs pastilles « Adaptable » / « Ne vous convient
                    pas » en fonction de ce qui est déclaré ici. Demande d'Emeni (2026-08-23). */}
                <div className="space-y-2">
                    <h3 className="text-xs font-bold text-content-muted uppercase tracking-widest">Restrictions alimentaires</h3>
                    <div className="grid grid-cols-2 gap-2">
                        {info.restriction_options.map((label) => (
                            <button key={label} type="button" onClick={() => toggleRestriction(label)}
                                className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${restrictions.includes(label) ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                    <input type="text" value={customRestriction} onChange={(e) => setCustomRestriction(e.target.value)}
                        placeholder="Autre restriction (optionnel)"
                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                </div>

                <div className="space-y-2">
                    <h3 className="text-xs font-bold text-content-muted uppercase tracking-widest">Allergies / ingrédients à éviter</h3>
                    {/* Saisie libre, sans liste déroulante : celle-ci proposait les libellés d'ingrédients tels
                        que saisis par l'établissement — « Base Burrata & Tomates », « Accompagnement &
                        Toppings » — qui ne veulent rien dire pour un convive déclarant une allergie. Il écrit
                        son mot à lui (« arachide », « lactose ») et le référentiel fait le rapprochement avec
                        le contenu réel des plats. Demande d'Emeni (2026-08-23). */}
                    <div className="flex items-center gap-2">
                        <input type="text" value={allergyDraft}
                            onChange={(e) => setAllergyDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAllergy(allergyDraft); } }}
                            placeholder="Ex : arachide, lactose, gluten..."
                            className="flex-1 bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                        <button type="button" onClick={() => addAllergy(allergyDraft)} disabled={!allergyDraft.trim()}
                            className="px-3 py-2.5 rounded-xl bg-brand-600 text-brand-fg text-sm font-bold disabled:opacity-40">
                            Ajouter
                        </button>
                    </div>
                    {allergies.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {allergies.map((a) => (
                                <span key={a} className="inline-flex items-center gap-1 text-xs font-semibold bg-surface-soft border border-border rounded-full pl-2.5 pr-1 py-1 text-content">
                                    {a}
                                    <button type="button" onClick={() => setAllergies((prev) => prev.filter((x) => x !== a))} className="p-0.5 rounded-full hover:bg-red-500/10 text-content-muted hover:text-red-400"><X size={11} /></button>
                                </span>
                            ))}
                        </div>
                    )}
                    <p className="text-[10px] text-content-muted italic">Le restaurant vérifiera votre déclaration avec les ingrédients de chaque plat — ceci n'est pas une garantie médicale.</p>
                </div>

                <div className="space-y-2">
                    <h3 className="text-xs font-bold text-content-muted uppercase tracking-widest">Maladie / grossesse nécessitant un régime particulier</h3>
                    <textarea value={medicalNote} onChange={(e) => setMedicalNote(e.target.value)} rows={2} maxLength={500}
                        placeholder="Facultatif — précisez ici si besoin (ex: diabète, grossesse, maladie nécessitant un régime adapté)"
                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                </div>

                {info.guest_choice_mode !== 'organisateur' && (
                <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${info.categories.length || 1}, minmax(0, 1fr))` }}>
                    {info.categories.map((cat) => {
                        // Une variante compte comme un plat sélectionnable à part entière (sa propre tuile,
                        // contour vert une fois choisie) — plus un simple lien sous une alerte. Demande
                        // explicite d'Emeni (2026-08-22) : "je veux que l'alternative soit sélectionnable
                        // comme un plat [...] quand il clique sur l'alternative le contour devient vert".
                        const selectedBaseDish = choices[cat.category] ? cat.dishes.find((d) => d.id === choices[cat.category]) : null;
                        const selectedVariantId = variantChoices[cat.category];
                        // Les avertissements liés à une restriction disparaissent une fois la variante qui la
                        // résout choisie ; ceux liés à une allergie sur un ingrédient restent affichés quelle
                        // que soit la variante (une variante ne change pas forcément les ingrédients).
                        const warnings = selectedBaseDish ? getDishWarnings(selectedBaseDish, selectedVariantId, cat.category) : [];
                        const suggestions = selectedBaseDish ? getRemovalSuggestions(selectedBaseDish) : [];
                        // Aucun plat de la categorie ne convient (tous "bloque") : sans issue, le convive
                        // reste bloque sur un choix impossible. Il peut alors declarer explicitement qu'il ne
                        // prendra rien dans cette categorie, ce qui debloque l'envoi et le signale a la
                        // cuisine/l'organisateur (au lieu d'un simple oubli silencieux).
                        const categoryFullyBlocked = cat.dishes.length > 0 && cat.dishes.every((d) => getDishStatus(d) === 'bloque');
                        const isSkipped = skippedCategories.includes(cat.category);
                        // Une demande déjà posée n'est prise en compte que si l'option est encore active :
                        // sinon, un établissement qui la désactive après coup laissait le convive sans AUCUNE
                        // issue — le bloc de demande disparaissait et le bouton "je ne prendrai pas de" restait
                        // masqué derrière la demande devenue invisible.
                        const isAltRequested = !!info.allow_alt_dish_request && altRequestedCategories.includes(cat.category);
                        return (
                            <div key={cat.category} className="space-y-2 min-w-0">
                                <h3 className="text-[11px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1 truncate"><Utensils size={11} className="shrink-0" /> {cat.category}</h3>
                                {isSkipped ? (
                                    <div className="rounded-xl border-2 border-green-500/40 bg-green-500/10 p-2.5 space-y-1.5">
                                        <p className="text-[11px] font-bold text-green-700 dark:text-green-400">Vous ne prendrez pas de {cat.category.toLowerCase()}</p>
                                        <button type="button" disabled={locked}
                                            onClick={() => setSkippedCategories((prev) => prev.filter((c) => c !== cat.category))}
                                            className="text-[10px] font-semibold underline text-content-muted hover:text-content disabled:opacity-60">
                                            Choisir un plat quand même
                                        </button>
                                    </div>
                                ) : (
                                <div className="space-y-1.5">
                                    {cat.dishes.map((d) => {
                                        const isBaseSelected = choices[cat.category] === d.id && !selectedVariantId;
                                        // Pastille visible dès la liste : le convive repère les plats qui lui
                                        // conviennent sans avoir à les sélectionner un par un pour découvrir
                                        // un avertissement. Un plat bloquant est signalé, jamais masqué —
                                        // l'établissement l'a mis à la carte, c'est au convive de voir
                                        // pourquoi il ne peut pas le prendre.
                                        const status = getDishStatus(d);
                                        return (
                                            <React.Fragment key={d.id}>
                                                <button type="button" disabled={locked}
                                                    onClick={() => { setChoices((prev) => ({ ...prev, [cat.category]: d.id })); setVariantChoices((prev) => { const next = { ...prev }; delete next[cat.category]; return next; }); setRemovals((prev) => ({ ...prev, [cat.category]: [] })); setSkippedCategories((prev) => prev.filter((c) => c !== cat.category)); }}
                                                    className={`w-full text-left px-2 py-2 rounded-xl border text-xs transition-all ${isBaseSelected ? 'bg-brand-600 border-brand-600 text-brand-fg' : status === 'bloque' ? 'bg-surface-soft border-red-500/40 text-content hover:border-red-500/60' : 'bg-surface-soft border-border text-content hover:border-brand-500/40'} ${locked ? 'opacity-60 cursor-not-allowed' : ''}`}>
                                                    <span className="font-bold block">{d.name}</span>
                                                    {d.description && <span className={`block text-[11px] ${isBaseSelected ? 'text-brand-fg/80' : 'text-content-muted'}`}>{d.description}</span>}
                                                    {status !== 'ok' && (
                                                        // Fond opaque même sur un plat sélectionné : en translucide sur le
                                                        // vert de sélection, le badge devenait illisible juste au moment où
                                                        // le convive en a le plus besoin.
                                                        <span className={`inline-flex items-center gap-1 mt-1 text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded ${status === 'bloque' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                                                            {status === 'bloque' ? <><X size={9} /> Ne vous convient pas</> : <><Info size={9} /> Adaptable</>}
                                                        </span>
                                                    )}
                                                </button>
                                                {/* Une variante n'apparaît comme choix que pour le plat ACTUELLEMENT
                                                    sélectionné par le convive (jamais dépliée sous tous les plats de la
                                                    catégorie — seulement celui déjà choisi), et seulement si une
                                                    restriction cochée le rend incompatible ET que cette variante la
                                                    résout. Le nom du plat n'est pas répété dans son propre libellé.
                                                    Demande explicite d'Emeni (2026-08-22) : "je veux vraiment qu'il
                                                    déplie les alternatives en fonction de ce qu'il a sélectionné comme
                                                    plat [...] on ne doit pas voir le nom du plat, on doit juste voir
                                                    l'alternative". */}
                                                {choices[cat.category] === d.id && (d.variants || [])
                                                    .filter((v) => restrictions.some((r) => d.incompatible_tags.includes(r) && v.resolves_restrictions.includes(r)))
                                                    .map((v) => {
                                                    const isVariantSelected = selectedVariantId === v.id;
                                                    return (
                                                        <button key={v.id} type="button" disabled={locked}
                                                            onClick={() => setVariantChoices((prev) => ({ ...prev, [cat.category]: v.id }))}
                                                            className={`w-full text-left px-2 py-2 ml-2.5 rounded-xl border-2 text-xs transition-all ${isVariantSelected ? 'bg-green-500/10 border-green-500 text-content' : 'bg-surface border-border text-content-muted hover:border-green-500/40'} ${locked ? 'opacity-60 cursor-not-allowed' : ''}`}>
                                                        <span className={`font-bold block ${isVariantSelected ? 'text-green-600 dark:text-green-400' : ''}`}>{v.label}</span>
                                                        </button>
                                                    );
                                                })}
                                {/* Retraits proposés pour le plat choisi : déduits des restrictions et allergies du
                                    convive, jamais saisis à l'avance par l'établissement. La ligne proposée est
                                    l'élément RETIRABLE — on ne peut pas ôter le beurre d'une sauce, on ôte la sauce.
                                    Idée d'Emeni (2026-08-23) : "s'il y a un sans lactose, il propose l'alternative
                                    sans sauce ; si c'est quelqu'un sans poisson, ça lui propose sans lieu". */}
                                {choices[cat.category] === d.id && suggestions.length > 0 && (
                                    <div className={`space-y-1 rounded-xl p-2 ml-2.5 border-2 ${suggestions.some((s) => !s.removable) ? 'bg-red-500/5 border-red-500/40' : 'bg-surface-soft border-border'}`}>
                                        <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest">
                                            {suggestions.some((s) => !s.removable) ? 'Ce plat ne peut pas être adapté' : 'Adapter ce plat'}
                                        </p>
                                        {suggestions.some((s) => !s.removable) && (
                                            // Une phrase par RAISON, pas une ligne par ingrédient : lister
                                            // « Stracciatella », « Crème », « Parmesan »… répétait cinq fois
                                            // la même information. Le convive a seulement besoin de savoir
                                            // que le plat ne convient pas à ce qu'il a déclaré. Demande
                                            // d'Emeni (2026-08-23).
                                            <p className="text-[11px] text-red-600 dark:text-red-400 font-semibold">
                                                Non compatible avec {Array.from(new Set(suggestions.filter((s) => !s.removable).map((s) => s.reason))).join(', ')}.
                                                <br />Merci de choisir un autre plat dans cette catégorie.
                                            </p>
                                        )}
                                        {suggestions.map((s) => {
                                            const checked = removalsFor(cat.category).some((r) => r.toLowerCase() === s.name.toLowerCase());
                                            if (!s.removable) return null; // déjà résumé par la phrase ci-dessus
                                            return (
                                                <label key={s.name} className={`flex items-start gap-1.5 text-[11px] rounded-lg px-1.5 py-1 ${checked ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'text-content'} ${locked ? 'opacity-60' : 'cursor-pointer'}`}>
                                                    <input type="checkbox" checked={checked} disabled={locked}
                                                        onChange={() => setRemovals((prev) => {
                                                            const current = prev[cat.category] || [];
                                                            const next = checked
                                                                ? current.filter((r) => r.toLowerCase() !== s.name.toLowerCase())
                                                                : [...current, s.name];
                                                            return { ...prev, [cat.category]: next };
                                                        })}
                                                        className="w-3.5 h-3.5 rounded border-border accent-green-600 mt-0.5 shrink-0" />
                                                    <span>
                                                        <span className="font-bold">Sans {s.name.toLowerCase()}</span>
                                                        <span className="block text-[10px] text-content-muted">{s.reason}</span>
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    {cat.dishes.length === 0 && <p className="text-[11px] text-content-muted italic">Plus de choix disponible.</p>}
                                </div>
                                )}

                                {categoryFullyBlocked && !isSkipped && (
                                    <div className="space-y-1.5">
                                        {/* Renoncer n'était pas une issue pour un convive simplement coincé :
                                            il pouvait seulement déclarer qu'il ne mangerait rien, sans moyen de
                                            dire qu'il aurait bien voulu quelque chose. Demander une alternative
                                            remonte explicitement le cas à l'établissement, qui peut alors lui
                                            proposer un plat pris ailleurs dans son catalogue (alt_dish_offer,
                                            déjà en place). Demande d'Emeni (2026-08-24), activable événement par
                                            événement — masqué quand l'établissement ne veut pas de demandes. */}
                                        {info.allow_alt_dish_request && (
                                            isAltRequested ? (
                                                <div className="rounded-xl border-2 border-brand-500/40 bg-brand-500/10 p-2.5 space-y-1">
                                                    <p className="text-[11px] font-bold text-brand-600 dark:text-brand-400">
                                                        Alternative demandée pour votre {cat.category.toLowerCase()}
                                                    </p>
                                                    <p className="text-[10px] text-content-muted">
                                                        L'organisateur vous répondra — pensez à valider votre réponse ci-dessous.
                                                    </p>
                                                    <button type="button" disabled={locked}
                                                        onClick={() => setAltRequestedCategories((prev) => prev.filter((c) => c !== cat.category))}
                                                        className="text-[10px] font-semibold underline text-content-muted hover:text-content disabled:opacity-60">
                                                        Annuler ma demande
                                                    </button>
                                                </div>
                                            ) : (
                                                <button type="button" disabled={locked}
                                                    onClick={() => { setAltRequestedCategories((prev) => [...prev, cat.category]); setChoices((prev) => { const next = { ...prev }; delete next[cat.category]; return next; }); }}
                                                    className="w-full px-2 py-2 rounded-xl border-2 border-brand-500/50 text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:bg-brand-500/10 disabled:opacity-60">
                                                    Aucun plat ne me convient — demander une alternative
                                                </button>
                                            )
                                        )}
                                        {!isAltRequested && (
                                            <button type="button" disabled={locked}
                                                onClick={() => { setSkippedCategories((prev) => [...prev, cat.category]); setChoices((prev) => { const next = { ...prev }; delete next[cat.category]; return next; }); setVariantChoices((prev) => { const next = { ...prev }; delete next[cat.category]; return next; }); setRemovals((prev) => { const next = { ...prev }; delete next[cat.category]; return next; }); }}
                                                className="w-full text-[11px] font-bold text-content-muted hover:text-content underline disabled:opacity-60">
                                                Je ne prendrai pas de {cat.category.toLowerCase()}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {warnings.length > 0 && (
                                    <div className="space-y-1">
                                        {warnings.map((w, i) => {
                                            // Les traces ont leur propre couleur (bleu informatif) et une icône
                                            // distincte de l'avertissement : mélangées à l'ambre des vraies
                                            // incompatibilités, elles auraient l'air d'un problème à résoudre
                                            // alors qu'elles ne bloquent rien et sont inévitables.
                                            // Ambre = une action du convive résout le problème (cocher le
                                            // retrait) ; rouge = impasse, il faut changer de plat. Le vert
                                            // d'avant laissait croire que c'était déjà réglé alors que rien
                                            // n'était encore transmis à la cuisine.
                                            const actionable = w.blocking && !!w.removalSuggestion;
                                            const colorClass = actionable
                                                ? 'text-amber-700 dark:text-amber-300'
                                                : w.blocking
                                                    ? 'text-red-600 dark:text-red-400'
                                                    : w.trace
                                                        ? 'text-sky-700 dark:text-sky-300'
                                                        : 'text-amber-600 dark:text-amber-400';
                                            const bgClass = actionable
                                                ? 'border-amber-500/40 bg-amber-500/10'
                                                : w.blocking
                                                    ? 'border-red-500/30 bg-red-500/10'
                                                    : w.trace
                                                        ? 'border-sky-500/30 bg-sky-500/10'
                                                        : 'border-amber-500/30 bg-amber-500/10';
                                            const Icon = w.trace ? Info : AlertTriangle;
                                            return (
                                                <div key={i} className={`rounded-lg border px-2 py-1.5 ${bgClass}`}>
                                                    <p className={`text-[10px] font-semibold flex items-start gap-1 ${colorClass}`}>
                                                        <Icon size={11} className="shrink-0 mt-0.5" />
                                                        {/* Plus de renvoi vers une "variante" : les adaptations sont
                                                            désormais les cases à cocher juste en dessous, et les
                                                            anciennes variantes sont en cours de retrait. */}
                                                        <span>{w.text}</span>
                                                    </p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                )}


                {blockingIssues.length > 0 && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 space-y-1">
                        {blockingIssues.map((msg, i) => <p key={i} className="text-xs font-semibold text-red-500">{msg}</p>)}
                    </div>
                )}

                {!locked && (
                    <button onClick={handleSubmit} disabled={submitting || blockingIssues.length > 0}
                        className="w-full py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm disabled:opacity-50">
                        {submitting ? 'Envoi...' : 'Envoyer mes choix'}
                    </button>
                )}

                <div className="flex justify-center"><PoweredByFooter /></div>
            </div>
        </div>
    );
};
